// 「我的設備」（SPEC #me-devices、#device-permissions-ui、#org-model-sources）：GET /v1/me/devices 與 GET /v1/me/orgs 是 VMS 的唯一資料來源。
//   - 三種來源：我自己的（own）、組織授權給我（org）、別人分享給我（shared）；預設範圍「全部」。
//   - 分組：own / org 依組織 → site → 家戶（只有一個 site 的組織省略 site 層）；分享給組織的設備另列該組織的「外部分享」；shared 依分享者。
//   - 每台的按鈕只看 actions（不自己推權限）；不能做的原因看 denied（文案查多語系表 deny.*）。
//   - App / VMS 依 plane 各打一次再合併（同現行家戶清單）。
// 這裡只有型別、抓取與純函式；React 狀態在 lib/scope.tsx。
import { KinioError, kinioFetch, planeBase } from '@kinio/web-sdk';
import type { Plane } from './planeApi';

export type Source = 'own' | 'org' | 'shared';
export const SOURCES: Source[] = ['own', 'org', 'shared'];

export interface MeCondition {
  kind: string;
  until?: string;
}

export interface MeAction {
  action: string;
  via: Source;
  /** home_role:admin / site_grant:… / share:… */
  grant_ref: string;
  condition?: MeCondition;
}

export interface MeDenied {
  action: string;
  /** org_mfa_required / share_expired / share_suspended / alarm_window / armed / schedule / not_granted / device_offline … */
  reason: string;
  detail?: Record<string, unknown>;
}

export interface MeShare {
  share_id: string;
  grantee_kind: 'user' | 'org';
  /** 分享給組織：被分享的組織（我是它的成員） */
  org_id?: string;
  sharer_ref: string;
  sharer_display_name?: string;
  expires_at: string | null;
}

export interface MeOtherSource {
  source: Source;
  org_id?: string;
  share_id?: string;
  sharer_display_name?: string;
}

export interface MeChild {
  device_ref: string;
  display_name?: string;
  actions: string[];
}

export interface MeDevice {
  device_ref: string;
  display_name: string;
  model?: string;
  category: string;
  online: boolean;
  presence: string;
  source: Source;
  org_id: string | null;
  site_id: string | null;
  home_id: string;
  room_id: string | null;
  share: MeShare | null;
  actions: MeAction[];
  can_manage: boolean;
  denied: MeDenied[];
  expires_at: string | null;
  other_sources: MeOtherSource[];
  children?: MeChild[];
  container_only: boolean;
  /** 經節點委派（P6）來的：歸在受託組織 D 的 orgs[].delegations[]（SPEC #me-devices-orgtree） */
  delegation?: MeDeviceDelegation | null;
}

export interface MeDeviceDelegation {
  delegation_id: string;
  /** 受託組織 D（分組用） */
  org_id: string;
  from_org_id: string;
  from_org_name: string;
  expires_at?: string | null;
}

/** orgs[D].delegations[]：sites 從委派節點起、不回 A 的上層。 */
export interface MeDelegationGroup {
  delegation_id: string;
  from_org_id: string;
  from_org_name: string;
  expires_at?: string | null;
  /** delegator_mfa_required：A 要求 MFA、這次登入沒有 → 不列設備 */
  notice?: string | null;
  sites?: MeSite[];
  homes?: MeHome[];
}

export interface MeSite {
  site_id: string;
  name: string;
  parent_site_id: string | null;
  /** 層級名稱原值（組織樹，SPEC #me-devices-orgtree） */
  level_label?: string | null;
}

export interface MeHome {
  home_id: string;
  display_name: string;
  site_id: string | null;
  timezone?: string | null;
  /** 家戶所在的 plane（平台 H 起才有；沒有就用組織的 plane） */
  env?: string;
  /** 我的家戶角色（owner / admin / member / guest；只經 site 授權到得了的為 null）。平台 H 起才有：undefined = 舊後端 */
  role?: string | null;
  /** 家戶的管理動作（owner / admin）。平台 H 起才有 */
  can_manage?: boolean;
  /** 從我的可見起點到家戶的節點名稱（組織樹） */
  node_path?: string[];
}

export interface MeOrg {
  org_id: string;
  kind: 'personal' | 'team';
  display_name: string;
  /** own = 我是 owner；org = 管理員 / 成員 / 訪客… */
  source: 'own' | 'org';
  role: string;
  /** mfa_required / org_suspended / ip_not_allowed / session_reauth；null = 正常 */
  notice: string | null;
  sites: MeSite[];
  homes: MeHome[];
  /** 委派給這個組織（我是受託組織 D 的成員）的分組 */
  delegations?: MeDelegationGroup[];
  /** 可達家戶 > 500：homes / sites 不回，導覽改用 /v1/me/tree（左側組織樹） */
  homes_truncated?: boolean;
}

export interface MeSharer {
  sharer_ref: string;
  kind: string;
  display_name?: string;
}

interface MeDevicesPage {
  devices: MeDevice[];
  orgs: MeOrg[];
  sharers: MeSharer[];
  access_revision: string;
  next_cursor?: string;
}

/** 帶 plane 的一台（同一個 home_id 只會在一個 plane）。 */
export type MyDevice = MeDevice & { env: string; api: string };
export type MyDevOrg = MeOrg & { env: string; api: string };

export interface MyDevicesData {
  devices: MyDevice[];
  orgs: MyDevOrg[];
  sharers: MeSharer[];
  /** 抓不到的 plane（顯示錯誤；那個 plane 的設備就不列） */
  failed: Plane[];
}

export const EMPTY_MY_DEVICES: MyDevicesData = { devices: [], orgs: [], sharers: [], failed: [] };

/** 一個 plane 的「我的設備」：翻完所有頁；授權在翻頁中途變了（409 AUTHORIZATION_CHANGED）就從頭重列（最多 3 次）。 */
export async function fetchMyDevices(p: Plane, fetchImpl: typeof kinioFetch = kinioFetch): Promise<Omit<MyDevicesData, 'failed'>> {
  for (let attempt = 0; ; attempt++) {
    try {
      const devices: MyDevice[] = [];
      let orgs: MeOrg[] = [];
      let sharers: MeSharer[] = [];
      let cursor = '';
      for (let page = 0; page < 50; page++) {
        const qs = new URLSearchParams({ limit: '500' });
        if (cursor) qs.set('cursor', cursor);
        const r = await fetchImpl<MeDevicesPage>(`${planeBase(p.api)}/v1/me/devices?${qs}`);
        for (const d of r?.devices ?? []) devices.push({ ...d, env: p.env, api: p.api });
        if (page === 0) {
          orgs = r?.orgs ?? [];
          sharers = r?.sharers ?? [];
        }
        cursor = r?.next_cursor ?? '';
        if (!cursor) break;
      }
      return { devices, orgs: orgs.map((o) => ({ ...o, env: p.env, api: p.api })), sharers };
    } catch (e) {
      if (attempt < 2 && e instanceof KinioError && e.status === 409 && e.code === 'AUTHORIZATION_CHANGED') continue;
      throw e;
    }
  }
}

/** 合併各 plane（plane 抓失敗 → failed）。 */
export function mergeMyDevices(planes: Plane[], results: (Omit<MyDevicesData, 'failed'> | undefined | null)[], errored: boolean[]): MyDevicesData {
  const out: MyDevicesData = { devices: [], orgs: [], sharers: [], failed: [] };
  const sharerSeen = new Set<string>();
  planes.forEach((p, i) => {
    const r = results[i];
    if (errored[i]) out.failed.push(p);
    if (!r) return;
    out.devices.push(...r.devices);
    out.orgs.push(...r.orgs);
    for (const s of r.sharers) {
      if (sharerSeen.has(s.sharer_ref)) continue;
      sharerSeen.add(s.sharer_ref);
      out.sharers.push(s);
    }
  });
  return out;
}

// ---- 批次命令（SPEC #device-permissions-ui、DP12） ----

export interface BatchCommandResult {
  device_ref: string;
  home_id: string;
  status: 'ok' | 'error';
  http_status: number;
  /** 那台單獨打時的錯誤回應（{error, message, detail?}） */
  error?: { error?: string; message?: string; detail?: Record<string, unknown> };
}

/**
 * POST /v1/me/devices/commands：跨來源選的設備一次送（每個 plane 一次、一次最多 100 台）；伺服器逐台驗權，
 * 沒權限的那幾台回原因、不中斷其他台。
 */
export async function batchCommand(
  api: string,
  devices: { home_id: string; device_ref: string }[],
  command: { op: string; id: string; payload?: Record<string, unknown>; wait_seconds?: number },
  fetchImpl: typeof kinioFetch = kinioFetch,
): Promise<BatchCommandResult[]> {
  const out: BatchCommandResult[] = [];
  for (let i = 0; i < devices.length; i += 100) {
    const r = await fetchImpl<{ results: BatchCommandResult[] }>(`${planeBase(api)}/v1/me/devices/commands`, {
      method: 'POST',
      body: { devices: devices.slice(i, i + 100), command },
    });
    out.push(...(r?.results ?? []));
  }
  return out;
}

// ---- 權限 ----

/** 這台現在能不能做 action（只看 actions，SPEC #device-permissions-ui）。 */
export function allows(d: Pick<MeDevice, 'actions'> | undefined | null, action: string): boolean {
  return !!d && d.actions.some((a) => a.action === action);
}

/** 這台不能做 action 的原因（有回 denied 才有；沒有就是「沒有授予」）。 */
export function denialOf(d: Pick<MeDevice, 'denied'> | undefined | null, action: string): MeDenied | null {
  return d?.denied.find((x) => x.action === action) ?? null;
}

/** 分組用的組織：分享給組織的設備歸被分享的組織（外部分享）；個人分享沒有組織。 */
export function groupOrgOf(d: Pick<MeDevice, 'source' | 'org_id' | 'share' | 'delegation'>): string {
  if (d.delegation?.org_id) return d.delegation.org_id;
  if (d.share?.grantee_kind === 'org' && d.share.org_id) return d.share.org_id;
  if (d.source === 'shared') return '';
  return d.org_id ?? '';
}

/** 經分享給組織進來的（組織的「外部分享」）。 */
export function isExternal(d: Pick<MeDevice, 'share'>): boolean {
  return d.share?.grantee_kind === 'org';
}

// ---- 範圍 ----

/**
 * 範圍鍵：
 *   all                全部（預設）
 *   src:own|org|shared 某一種來源
 *   org:<org_id>       一個組織（含分享給它的外部分享）
 *   site:<site_id>     一個 site 子樹
 *   home:<home_id>     一個家戶
 *   ext:<org_id>       一個組織收到的外部分享
 *   sharer:<ref>       某個分享者分享給我的
 */
export type ScopeKey = string;

export function siteSubtree(sites: MeSite[], root: string): Set<string> {
  const out = new Set<string>([root]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of sites) {
      if (s.parent_site_id && out.has(s.parent_site_id) && !out.has(s.site_id)) {
        out.add(s.site_id);
        grew = true;
      }
    }
  }
  return out;
}

function homeSite(orgs: MyDevOrg[], homeId: string): { org: MyDevOrg; siteId: string | null } | null {
  for (const o of orgs) {
    const h = o.homes.find((x) => x.home_id === homeId);
    if (h) return { org: o, siteId: h.site_id };
  }
  return null;
}

/** 範圍內的設備。 */
export function devicesInScope(data: MyDevicesData, scope: ScopeKey): MyDevice[] {
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  switch (kind) {
    case 'src':
      return data.devices.filter((d) => d.source === id);
    case 'org':
      return data.devices.filter((d) => groupOrgOf(d) === id);
    case 'ext':
      return data.devices.filter((d) => isExternal(d) && d.share?.org_id === id);
    case 'site': {
      const org = data.orgs.find((o) => o.sites.some((s) => s.site_id === id));
      if (!org) return [];
      const sub = siteSubtree(org.sites, id);
      return data.devices.filter((d) => {
        if (isExternal(d) || d.source === 'shared' || d.org_id !== org.org_id) return false;
        const hs = homeSite(data.orgs, d.home_id)?.siteId ?? null;
        return (!!hs && sub.has(hs)) || (!!d.site_id && sub.has(d.site_id));
      });
    }
    case 'home':
      return data.devices.filter((d) => d.home_id === id);
    case 'sharer':
      return data.devices.filter((d) => d.source === 'shared' && d.share?.sharer_ref === id);
    default:
      return data.devices;
  }
}

/** 範圍鍵還有效嗎（組織 / 家戶 / 分享者已經不在 → 回「全部」）。 */
export function validScope(data: MyDevicesData, scope: ScopeKey): boolean {
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  switch (kind) {
    case 'all':
      return true;
    case 'src':
      return (SOURCES as string[]).includes(id);
    case 'org':
      return data.orgs.some((o) => o.org_id === id);
    case 'ext':
      return data.devices.some((d) => isExternal(d) && d.share?.org_id === id);
    case 'site':
      return data.orgs.some((o) => o.sites.some((s) => s.site_id === id));
    case 'home':
      return data.orgs.some((o) => o.homes.some((h) => h.home_id === id)) || data.devices.some((d) => d.home_id === id);
    case 'sharer':
      return data.devices.some((d) => d.share?.sharer_ref === id);
  }
  return false;
}

/** 範圍指到的組織（組織管理類頁面一次只對一個組織）。 */
export function orgOfScope(data: MyDevicesData, scope: ScopeKey): string | null {
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  if (kind === 'org' || kind === 'ext') return id;
  if (kind === 'site') return data.orgs.find((o) => o.sites.some((s) => s.site_id === id))?.org_id ?? null;
  if (kind === 'home') return homeSite(data.orgs, id)?.org.org_id ?? null;
  return null;
}

// ---- 家戶（頁面用：一個家戶一個 API 端點） ----

export interface ScopeHomeInfo {
  home_id: string;
  display_name: string;
  env: string;
  api: string;
  source: Source;
  org_id?: string;
  org_kind?: 'personal' | 'team';
  org_role?: string;
  /** 「我的設備」回的家戶角色（null = 只經 site 授權）；undefined = 舊後端沒回，頁面要退回 GET /v1/homes/{id} */
  home_role?: string | null;
  site_id?: string;
  timezone?: string;
  /** 只經分享進來的家戶（不是我任何組織的家戶）：看不到家戶名稱與成員 */
  external: boolean;
  /** external 時的顯示名稱（分享者） */
  sharer_name?: string;
  /** 這個家戶裡有任何一台我可以管理 */
  can_manage: boolean;
  /** 組織要求 MFA 等：組織照樣列出、設備不列 */
  notice?: string | null;
}

/** 家戶清單：我組織裡的家戶（含沒有設備的，導覽用）＋只經分享看到設備的家戶。 */
export function scopeHomes(data: MyDevicesData): ScopeHomeInfo[] {
  const out = new Map<string, ScopeHomeInfo>();
  for (const o of data.orgs) {
    for (const h of o.homes) {
      out.set(h.home_id, {
        home_id: h.home_id,
        display_name: h.display_name,
        env: h.env || o.env,
        api: o.api,
        source: o.source,
        org_id: o.org_id,
        org_kind: o.kind,
        org_role: o.role,
        home_role: h.role,
        site_id: h.site_id ?? undefined,
        timezone: h.timezone ?? undefined,
        external: false,
        can_manage: !!h.can_manage,
        notice: o.notice,
      });
    }
  }
  // 委派來的家戶（P6）：沒有家戶角色、不能管理；歸受託組織分組，但家戶屬於委派方
  for (const o of data.orgs) {
    for (const g of o.delegations ?? []) {
      if (g.notice) continue;
      for (const h of g.homes ?? []) {
        if (out.has(h.home_id)) continue;
        out.set(h.home_id, {
          home_id: h.home_id,
          display_name: h.display_name,
          env: h.env || o.env,
          api: o.api,
          source: o.source,
          org_id: g.from_org_id,
          org_kind: 'team',
          home_role: null,
          site_id: h.site_id ?? undefined,
          timezone: h.timezone ?? undefined,
          external: false,
          can_manage: false,
          notice: null,
        });
      }
    }
  }
  for (const d of data.devices) {
    const cur = out.get(d.home_id);
    if (cur) {
      if (d.can_manage) cur.can_manage = true;
      continue;
    }
    const name = d.share?.sharer_display_name || '';
    out.set(d.home_id, {
      home_id: d.home_id,
      display_name: name,
      env: d.env,
      api: d.api,
      source: d.source,
      external: true,
      sharer_name: name,
      can_manage: d.can_manage,
    });
  }
  return [...out.values()];
}

/** 家戶在範圍內嗎：範圍內有它的設備，或範圍涵蓋這個組織家戶（空家戶也列，導覽用）。 */
export function homesInScope(data: MyDevicesData, homes: ScopeHomeInfo[], scope: ScopeKey): ScopeHomeInfo[] {
  const devHomes = new Set(devicesInScope(data, scope).map((d) => d.home_id));
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  return homes.filter((h) => {
    if (devHomes.has(h.home_id)) return true;
    if (h.external) return false;
    switch (kind) {
      case 'all':
        return true;
      case 'src':
        return h.source === id;
      case 'org':
        return h.org_id === id;
      case 'home':
        return h.home_id === id;
      case 'site': {
        const org = data.orgs.find((o) => o.org_id === h.org_id);
        return !!org && !!h.site_id && org.sites.some((s) => s.site_id === id) && siteSubtree(org.sites, id).has(h.site_id);
      }
    }
    return false;
  });
}

/**
 * 家戶角色（只給頁面決定 owner 專屬項目，例如錄影設定、帳單；每台的按鈕看 actions）。
 * 平台 H 起「我的設備」的 orgs[].homes[] 直接回 role（null = 只經 site 授權 → 沒有家戶角色）。
 * 舊後端沒回時退回：GET /v1/homes/{id} 的 home.role（detailRole）；再沒有就個人組織依組織角色預設
 * （owner → owner、org_admin → admin…，SPEC #org-model-roles）、team 組織用設備的 can_manage 推「管理員」。
 */
export function homeRoleOf(h: ScopeHomeInfo, detailRole?: string): string {
  if (h.home_role !== undefined) return h.home_role ?? '';
  if (detailRole) return detailRole;
  if (h.external) return '';
  if (h.org_kind === 'personal') {
    const m: Record<string, string> = { owner: 'owner', org_admin: 'admin', member: 'member', guest: 'guest' };
    return m[h.org_role ?? ''] ?? '';
  }
  return h.can_manage ? 'admin' : '';
}

// ---- 分組（設備樹、範圍切換器共用） ----

export interface ScopeNode {
  key: ScopeKey;
  kind: 'source' | 'org' | 'site' | 'home' | 'external' | 'sharer' | 'delegation';
  /** 顯示名稱；source / external 由畫面翻譯（label 空字串） */
  label: string;
  source: Source;
  depth: number;
  /** 範圍內的設備數 */
  count: number;
  notice?: string | null;
  orgKind?: 'personal' | 'team';
}

/** 組織的根節點（parent 空的 site）：畫面上不另成一列，組織那一列就是「整個組織」（SPEC #vms-web-220 組織根節點，第二輪 B2-04）。 */
//   只在恰好一個 parent 空的 site 時才當根節點（組織樹的資料模型）；舊的平面場域（好幾個頂層 site）照舊逐一列出。
export function rootSiteIds(org: Pick<MeOrg, 'sites'>): Set<string> {
  const tops = org.sites.filter((s) => !s.parent_site_id);
  return new Set(tops.length === 1 ? [tops[0].site_id] : []);
}

/** 場域列（深度優先）；根節點不列、它底下的場域直接從深度 0 起算。 */
function siteRowsOf(org: MeOrg): { site: MeSite; depth: number }[] {
  const out: { site: MeSite; depth: number }[] = [];
  const ids = new Set(org.sites.map((s) => s.site_id));
  const roots = rootSiteIds(org);
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const s of org.sites
      .filter((x) => (x.parent_site_id && ids.has(x.parent_site_id) ? x.parent_site_id : null) === parent)
      .sort((a, b) => a.name.localeCompare(b.name))) {
      if (seen.has(s.site_id)) continue;
      seen.add(s.site_id);
      if (roots.has(s.site_id)) {
        walk(s.site_id, depth); // 根節點不另顯示
        continue;
      }
      out.push({ site: s, depth });
      walk(s.site_id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** 範圍鍵正規化：指到組織根節點的 site:<根> = 整個組織（org:<id>）。 */
export function canonicalScope(data: MyDevicesData, scope: ScopeKey): ScopeKey {
  if (!scope.startsWith('site:')) return scope;
  const id = scope.slice(5);
  const o = data.orgs.find((x) => rootSiteIds(x).has(id));
  return o ? 'org:' + o.org_id : scope;
}

/** 依 SPEC #me-devices 的分組展平成樹狀節點（顯示順序：own → org → shared；組織名稱、site、家戶名稱）。 */
export function scopeTree(data: MyDevicesData): ScopeNode[] {
  const out: ScopeNode[] = [];
  const count = (k: ScopeKey) => devicesInScope(data, k).length;
  for (const src of SOURCES) {
    const node: ScopeNode = { key: 'src:' + src, kind: 'source', label: '', source: src, depth: 0, count: count('src:' + src) };
    const children: ScopeNode[] = [];
    if (src === 'shared') {
      const refs = new Map<string, string>();
      for (const d of data.devices) if (d.source === 'shared' && d.share) refs.set(d.share.sharer_ref, d.share.sharer_display_name ?? '');
      for (const s of data.sharers) if (refs.has(s.sharer_ref) && s.display_name) refs.set(s.sharer_ref, s.display_name);
      for (const [ref, name] of [...refs.entries()].sort((a, b) => a[1].localeCompare(b[1]))) {
        children.push({ key: 'sharer:' + ref, kind: 'sharer', label: name, source: src, depth: 1, count: count('sharer:' + ref) });
      }
    } else {
      const orgs = data.orgs
        .filter((o) => o.source === src)
        .sort((a, b) => (a.kind === b.kind ? a.display_name.localeCompare(b.display_name) : a.kind === 'personal' ? -1 : 1));
      for (const o of orgs) {
        children.push({ key: 'org:' + o.org_id, kind: 'org', label: o.display_name, source: src, depth: 1, count: count('org:' + o.org_id), notice: o.notice, orgKind: o.kind });
        if (o.notice) continue; // 組織要求 MFA / 停用：不列它的家戶與設備
        const usedSites = new Set(o.homes.map((h) => h.site_id).filter((x): x is string => !!x));
        const showSites = usedSites.size > 1;
        const homesAt = (siteId: string | null) => o.homes.filter((h) => (showSites ? h.site_id === siteId : true)).sort((a, b) => a.display_name.localeCompare(b.display_name));
        if (showSites) {
          // 直接掛在根節點的家戶排在組織下（根節點不另成一列）
          const roots = rootSiteIds(o);
          for (const h of o.homes.filter((x) => !!x.site_id && roots.has(x.site_id)).sort((a, b) => a.display_name.localeCompare(b.display_name)))
            children.push({ key: 'home:' + h.home_id, kind: 'home', label: h.display_name, source: src, depth: 2, count: count('home:' + h.home_id) });
          for (const { site, depth } of siteRowsOf(o)) {
            const sub = siteSubtree(o.sites, site.site_id);
            if (![...usedSites].some((x) => sub.has(x))) continue;
            children.push({ key: 'site:' + site.site_id, kind: 'site', label: site.name, source: src, depth: 2 + depth, count: count('site:' + site.site_id) });
            for (const h of homesAt(site.site_id)) children.push({ key: 'home:' + h.home_id, kind: 'home', label: h.display_name, source: src, depth: 3 + depth, count: count('home:' + h.home_id) });
          }
        } else {
          for (const h of homesAt(null)) children.push({ key: 'home:' + h.home_id, kind: 'home', label: h.display_name, source: src, depth: 2, count: count('home:' + h.home_id) });
        }
        // 委派給這個組織的（範圍鍵 dlg:，伺服器展開；數字在這裡就算得出來）
        for (const g of o.delegations ?? []) {
          const n = data.devices.filter((d) => d.delegation?.delegation_id === g.delegation_id).length;
          children.push({ key: 'dlg:' + g.delegation_id, kind: 'delegation', label: g.from_org_name, source: src, depth: 2, count: n, notice: g.notice ?? null });
        }
        const ext = count('ext:' + o.org_id);
        if (ext) children.push({ key: 'ext:' + o.org_id, kind: 'external', label: '', source: src, depth: 2, count: ext });
      }
    }
    if (node.count || children.length) out.push(node, ...children);
  }
  return out;
}

/** 一個節點和它的上層（來源 → 組織 → site → 家戶），給「依來源」檢視的群組標題。 */
export function nodePath(tree: ScopeNode[], key: ScopeKey): ScopeNode[] {
  const i = tree.findIndex((n) => n.key === key);
  if (i < 0) return [];
  const out = [tree[i]];
  let depth = tree[i].depth;
  for (let j = i - 1; j >= 0 && depth > 0; j--) {
    if (tree[j].depth < depth) {
      out.unshift(tree[j]);
      depth = tree[j].depth;
    }
  }
  return out;
}

/** 設備表格「依來源」檢視：一台設備歸在哪個葉節點（家戶 / 外部分享 / 分享者），order 給排序用。 */
export function leafOf(d: MeDevice, tree: ScopeNode[]): { key: ScopeKey; order: number } {
  const want = isExternal(d) ? 'ext:' + d.share?.org_id : d.source === 'shared' ? 'sharer:' + d.share?.sharer_ref : 'home:' + d.home_id;
  const i = tree.findIndex((n) => n.key === want);
  return { key: want, order: i < 0 ? tree.length : i };
}
